import React from "react";
import { useTranslation } from "react-i18next";
import { dateTimetoIso, isComponentEnabledForUser, isComponentVisibleForUser, truncateForDisplay } from "../../../utils";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { UC0180, UC0181 } from "../../../utils/constants";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import InfoIcon from '@mui/icons-material/Info';

import { Avatar, Button, IconButton, useTheme } from "@mui/material";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import LicensePlate from "../../../components/common/LicensePlate";
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import CustomTable from "../../../components/common/CustomTable"
import BugReportIcon from '@mui/icons-material/BugReport';
import { NavLink, useNavigate } from "react-router-dom";

function ParkingActiveList(props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const {
        parkingActive,
        userUseCases,
        handleParkingActiveDetail,
        table,
    } = props;

    const cellStyle = {
        paddingLeft: 4,
        paddingRight: 4,
        lineHeight: '19px',
        color: '#1B1B28',
    };

    const columns = [
        {
            title: t('ID'),
            field: 'id',
            headerStyle: { textAlign: 'left', paddingLeft: 5 },
            cellStyle: {
                ...cellStyle,
                align: 'center', paddingLeft: 5,
                color: (theme) => theme.palette.background.contrastText,
            },
            width: "20%",
            render: (rowData) => rowData.email ? rowData.email : rowData.applicationId
        },
        {
            title: t('DATE_FROM'),
            field: 'dateFrom',
            headerStyle: { textAlign: 'left', paddingLeft: 5 },
            cellStyle: {
                ...cellStyle,
                align: 'left',
                color: (theme) => theme.palette.background.contrastText,
            },
            width: "20%",
            render: (rowData) => (
                dateTimetoIso(rowData.dateFrom)
            )
        },
        {
            title: t('DATE_TO'),
            field: 'dateTo',
            headerStyle: { textAlign: 'left', paddingLeft: 5 },
            cellStyle: {
                ...cellStyle,
                align: 'left',
                color: (theme) => theme.palette.background.contrastText,
            },
            width: "20%",
            render: (rowData) => (
                dateTimetoIso(rowData.dateTo)
            ),
        },
        {
            title: t('LPN'),
            field: 'lpn',
            headerStyle: { textAlign: 'left', paddingLeft: 5 },
            cellStyle: {
                ...cellStyle,
                align: 'left',
                color: (theme) => theme.palette.background.contrastText,
            },
            width: "10%",
            render: (rowData) => (
                rowData.favoriteCar && rowData.favoriteCar.lpn ? rowData.favoriteCar.lpn : rowData.lpn
            ),
        },
        {
            title: t('PLACE'),
            field: 'place',
            headerStyle: { textAlign: 'left', paddingLeft: 5, },
            cellStyle: {
                ...cellStyle,
                align: 'left',
                color: (theme) => theme.palette.background.contrastText,
            },
            width: "20%",
            render: (rowData) => (
                rowData.parkingPlace.name
            ),
        },
        {
            title: '',
            field: 'action',
            sorting: false,
            cellStyle: {
                ...cellStyle,
                padding: '12px 4px',
            },
            headerStyle: {
                padding: '18px 34px',
            },
            width: "10%",
            render: (rowData) => (
                <div >
                    {isComponentVisibleForUser(UC0181, userUseCases) &&
                        <Button
                            onClick={() => { handleParkingActiveDetail(rowData) }}
                            disabled={!isComponentEnabledForUser(UC0181, userUseCases)}
                        >
                            <InfoIcon />
                        </Button>
                    }
                    {isComponentEnabledForUser(UC0180, userUseCases) &&
                        <Tooltip title={t('SHOW_RECEIPTS')}>
                            <IconButton
                                fullWidth
                                color="primary"
                                sx={{ padding: 0 }}
                                onClick={() => navigate(`/${process.env.REACT_APP_PATH}/backoffice/receipt?parkingActiveId=${rowData.parkingActiveId}`)}>
                                <ReceiptIcon />
                            </IconButton>
                        </Tooltip>
                    }
                </div>
            ),
        },

    ];

    const tableOptions = {
        headerStyle: {
            padding: '15px 8px 15px 34px',
            lineHeight: '12px',
            color: (theme) => theme.palette.background.contrastText,
            textAlign: 'center',
        },
        paging: false
    };
    const renderParkingType = (param) => {
        if (param.parkingPlace.type === "ON_STREET") {
            return (
                <Tooltip title={t("ONSTREET")}>
                    <img src="/onstreet.png" alt="Onstreet parking icon" height="36px" />
                </Tooltip>
            );
        } else { // OFF_STREET
            return (
                <Tooltip title={t("OFFSTREET")}>
                    <img src="/offstreet.png" alt="Offstreet parking icon" height="36px" />
                </Tooltip>
            );
        }
    };

    const CARD_SIZE = 400;

    const Row = ({ index, data, style }) => {
        const { receipts: parkingActives, itemsPerRow } = data;

        if (!parkingActives) {
            return null;
        }

        const items = [];
        const fromIndex = index * itemsPerRow;

        const toIndex = Math.min(fromIndex + itemsPerRow, parkingActives.length);

        for (let i = fromIndex; i < toIndex; i++) {
            let parkingActiveItem = parkingActives[i];
            items.push(
                <Paper
                    key={parkingActiveItem.userId}
                    elevation={0}
                    sx={{
                        width: CARD_SIZE,
                        height: 150,
                        padding: 1,
                        margin: 1,
                        backgroundColor: (theme) =>
                            theme.palette.background.paper,
                        boxShadow: `1px 1px 20px 1px ${theme.palette.shadow}`,
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        marginLeft="0px"
                        style={{ padding: 5 }}
                    >
                        <Grid item xs={2}>
                            <div
                                style={{
                                    paddingLeft: "6px",
                                    paddingTop: "6px",
                                }}
                            >
                                {renderParkingType(parkingActiveItem)}
                            </div>
                        </Grid>
                        <Grid item xs={9}>
                            <Tooltip title={t("ADDRESS")}>
                                <Typography
                                    marginLeft="10px"
                                    marginTop="3px"
                                    onClick={() => {
                                        isComponentVisibleForUser(
                                            UC0181,
                                            userUseCases,
                                        ) &&
                                            handleParkingActiveDetail(parkingActiveItem);
                                    }}
                                    sx={{
                                        cursor: isComponentVisibleForUser(
                                            UC0181,
                                            userUseCases,
                                        )
                                            ? "pointer"
                                            : "default",
                                        fontWeight: isComponentVisibleForUser(
                                            UC0181,
                                            userUseCases,
                                        )
                                            ? 600
                                            : "inherit",
                                    }}
                                    variant="body1"
                                >
                                    {parkingActiveItem.parkingPlace.name}
                                </Typography>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={1}>
                            {!parkingActiveItem.mbsId &&
                                <Tooltip title={t('MBS_ERROR')}>
                                    <BugReportIcon></BugReportIcon>
                                </Tooltip>
                            }
                        </Grid>

                        <Grid item xs={5} sx={{ paddingTop: 1 }}>
                            <Typography
                                marginLeft="10px"
                                variant="h6"
                            >
                                {truncateForDisplay(parkingActiveItem.lpn, 9)}
                            </Typography>
                        </Grid>

                        <Grid item xs={6} sx={{ paddingTop: 1 }}>
                            <Typography
                                variant="body2"
                            >
                                {truncateForDisplay(parkingActiveItem.email ? parkingActiveItem.email : parkingActiveItem.applicationId, 30)}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ paddingTop: 1 }}>
                            <Tooltip title={t("PAYMENT_STATE") + ' - ' + t(parkingActiveItem.state)}> {parkingActiveItem.state == 'PAID' ? <PriceCheckIcon color="secondary" /> : <MoneyOffIcon color="error" />}</Tooltip>
                        </Grid>

                        <Grid item xs={11} sx={{ paddingTop: 1 }}>
                            <Typography
                                marginLeft="10px"
                                variant="body2"
                            >
                                {t('FROM') + ': ' + dateTimetoIso(parkingActiveItem.dateFrom) + ' - ' + t('TO') + ': ' + dateTimetoIso(parkingActiveItem.dateTo)}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ paddingTop: 1 }}>
                            {isComponentEnabledForUser(UC0180, userUseCases) &&
                                <Tooltip title={t('SHOW_RECEIPTS')}>
                                    <IconButton
                                        fullWidth
                                        color="primary"
                                        sx={{ padding: 0 }}
                                        onClick={() => navigate(`/${process.env.REACT_APP_PATH}/backoffice/receipt?parkingActiveId=${parkingActiveItem.parkingActiveId}`)}>
                                        <ReceiptIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        </Grid>
                    </Grid>
                </Paper>,
            );
        }

        return (
            <div style={style}>
                <div
                    style={{
                        display: "flex",
                        padding: "20px",
                        justifyContent: "flex-start",
                        height: "100%",
                    }}
                >
                    {items}
                </div>
            </div>
        );
    };

    return (
        <div style={{ marginTop: "10px", height: "calc(100vh - 200px)" }}>
            {table &&
                <div style={{ padding: '30px' }}>
                    <CustomTable
                        title=""
                        columns={columns}
                        data={parkingActive}
                        options={tableOptions}
                        isLoading={parkingActive ? false : true}
                        loadAllData={false}
                    />
                </div>
            }

            {!table &&
                <AutoSizer>
                    {({ height, width }) => {
                        const itemsPerRow = Math.floor(width / CARD_SIZE) || 1; // A calculation to establish how many cards will go on each row.

                        // The || 1 part is a simple hack that makes it work in a really small viewport (if someone totally collapses the window)

                        const rowCount = Math.ceil(parkingActive.length / itemsPerRow); // List will need the number of rows in order to be able to properly know what to render and what not to
                        return (
                            <div>
                                <List
                                    height={height}
                                    itemCount={rowCount}
                                    itemData={{ itemsPerRow, receipts: parkingActive }}
                                    itemSize={170}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            </div>
                        );
                    }}
                </AutoSizer>
            }
        </div>
    );
}

export default ParkingActiveList;
